import React from "react";
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useCreateProjectFromStartup } from '@/hooks/useCreateProjectFromStartup';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SEOHead } from '@/components/SEOHead';
import { ShareButton } from '@/components/ShareButton';
import { CollapsibleSection } from '@/components/startup-detail/CollapsibleSection';
import { MarketSignalsRow } from '@/components/startup-detail/MarketSignalsRow';
import { ARRHighlightCard } from '@/components/startup-detail/ARRHighlightCard';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  TrendingUp, 
  TrendingDown, 
  Minus,
  Sparkles,
  Youtube,
  Newspaper,
  Target,
  Rocket,
  Users,
  DollarSign,
  BarChart3,
  Lightbulb,
  Heart,
  ExternalLink,
  BookOpen,
  MessageSquare,
  Search,
  Zap,
  RefreshCw,
  Activity,
  CreditCard,
  Brain,
  ShoppingCart,
  TrendingUpIcon,
  Loader2,
  Wand2,
  Briefcase,
  AlertTriangle,
  Calculator,
  Megaphone,
  Server,
  Shield,
  PiggyBank,
  Clock,
  Crosshair
} from 'lucide-react';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface LiveTrendData {
  keyword: string;
  timeline: { date: string; value: number }[];
  average_interest: number;
  trend_direction: string;
}

interface LiveKeywordData {
  keywords: {
    name: string;
    volume: number;
    cpc: number;
    competition: number;
  }[];
}

interface Analysis {
  overview: {
    title: string;
    tagline: string;
    category: string;
    tags: string[];
    source: string;
    sourceUrl?: string;
  };
  metrics: {
    trendScore: number;
    searchMomentum: number;
    momentumDirection: 'up' | 'down' | 'stable';
  };
  trendData: {
    curve: Array<{ date: string; value: number }>;
    last90Days: Array<{ date: string; value: number }>;
  };
  insights?: {
    whyNow?: string;
    proofSignals?: string;
    marketGap?: string;
    executionPlan?: {
      validation: string[];
      mvp: string[];
      growth: string[];
    };
    valueEquation?: string;
    acpFramework?: {
      authority: string;
      community: string;
      product: string;
    };
    valueLadder?: Array<{
      tier: string;
      offering: string;
    }>;
    businessFit?: string;
    scores?: {
      opportunity: number;
      problem: number;
      feasibility: number;
      whyNowScore: number;
      goToMarket: number;
      executionDifficulty: number;
      revenuePotential: number;
    };
    tam?: string;
    sam?: string;
    mrrRange?: string;
    arrProjections?: {
      conservative: {
        year1: string;
        year2: string;
        year3: string;
        assumptions: string;
      };
      base: {
        year1: string;
        year2: string;
        year3: string;
        assumptions: string;
      };
      aggressive: {
        year1: string;
        year2: string;
        year3: string;
        assumptions: string;
      };
    };
    unitEconomics?: {
      ltv: string;
      cac: string;
      ltvCacRatio: string;
      paybackPeriod: string;
      grossMargin: string;
      explanation: string;
    };
    goToMarketStrategy?: {
      primaryChannels: Array<{
        channel: string;
        description: string;
      }>;
      icpDefinition: string;
      gtmFit: string;
      launchStrategy: string;
    };
    estimatedCosts?: {
      mvpBuildCost: {
        range: string;
        breakdown: string;
      };
      monthlyOperating: {
        range: string;
        breakdown: string;
      };
      toolingStack: Array<{
        category: string;
        tools: string;
        cost: string;
      }>;
      runwayCalculation: string;
    };
    risksAndFailureModes?: {
      marketRisks: Array<{
        risk: string;
        likelihood: string;
        impact: string;
        mitigation: string;
      }>;
      executionRisks: Array<{
        risk: string;
        likelihood: string;
        impact: string;
        mitigation: string;
      }>;
      pricingRisks: Array<{
        risk: string;
        likelihood: string;
        impact: string;
        mitigation: string;
      }>;
      competitiveRisks?: Array<{
        risk: string;
        likelihood: string;
        impact: string;
        mitigation: string;
      }>;
      summary: string;
    };
    monetizationOpportunities?: Array<{
      name: string;
      description: string;
      pricing: string;
      scalability: string;
      iconType: 'subscription' | 'ai-service' | 'analytics' | 'marketplace' | 'freemium';
    }>;
    marketSignals?: {
      summary: string;
      signals: string[];
    };
    aiSummary?: string;
  };
  externalData?: {
    youtube: {
      query?: string;
      videos: Array<{
        videoId: string;
        title: string;
        channelTitle: string;
        publishedAt: string;
        thumbnail: string;
        url: string;
      }>;
    };
    news: {
      query?: string;
      articles: Array<{
        title: string;
        description: string;
        url: string;
        source: string;
        publishedAt: string;
        urlToImage?: string;
      }>;
    };
  };
  generatedAt?: string;
}

export default function StartupDetailView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { createProjectFromIdea, isCreating, progress } = useCreateProjectFromStartup();
  const [analysis, setAnalysis] = useState<Analysis | null>(null);
  const [startup, setStartup] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [trendPeriod, setTrendPeriod] = useState<'6m' | '1y' | '5y'>('1y');
  const [liveTrendData, setLiveTrendData] = useState<LiveTrendData | null>(null);
  const [liveKeywordData, setLiveKeywordData] = useState<LiveKeywordData | null>(null);
  const [loadingLiveData, setLoadingLiveData] = useState(false);
  const [selectedKeyword, setSelectedKeyword] = useState<string | null>(null);
  const [trendLastUpdated, setTrendLastUpdated] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchStartup();
      fetchAnalysis();
      trackView();
      checkIfSaved();
    }
  }, [id, user]);

  const fetchStartup = async () => {
    try {
      const { data, error } = await supabase
        .from('live_startups')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      setStartup(data);
    } catch (error) {
      console.error('Error fetching startup:', error);
    }
  };

  const trackView = async () => {
    try {
      await supabase.from("startup_interactions").insert({
        user_id: user?.id || null,
        startup_id: id,
        interaction_type: "view",
      });
    } catch (error) {
      console.error("Failed to track view:", error);
    }
  };

  const checkIfSaved = async () => {
    if (!user || !id) return;
    
    try {
      const { data, error } = await supabase
        .from('ideas')
        .select('id')
        .eq('user_id', user.id)
        .eq('source_url', `${window.location.origin}/startups/${id}`)
        .maybeSingle();
        
      if (!error && data) {
        setIsSaved(true);
      }
    } catch (error) {
      console.error('Error checking saved status:', error);
    }
  };

  const saveToIdeas = async () => {
    if (!user) {
      toast.error('Please sign in to save startups');
      navigate('/auth');
      return;
    }

    if (!analysis) {
      toast.error('Analysis not loaded yet');
      return;
    }

    try {
      const { data: newIdea, error } = await supabase.from('ideas').insert({
        user_id: user.id,
        title: analysis.overview.title,
        tagline: analysis.overview.tagline,
        description: analysis.insights?.whyNow || analysis.overview.tagline,
        problem: '',
        solution: '',
        icp: '',
        niche: analysis.overview.category || 'General',
        tags: analysis.overview.tags,
        source_url: `${window.location.origin}/startups/${id}`,
      }).select().single();

      if (error) throw error;

      // Immediately enrich the saved idea
      if (newIdea) {
        await supabase.functions.invoke('generate-idea-report', {
          body: {
            ideaId: newIdea.id,
            title: newIdea.title,
            description: newIdea.description || newIdea.title,
            niche: newIdea.niche,
            tags: newIdea.tags || [],
          }
        });
      }

      setIsSaved(true);
      toast.success('Saved and enriched to your dashboard!');
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save startup');
    }
  };

  const fetchAnalysis = async () => {
    try {
      setLoading(true);
      
      const { data: cachedAnalysis } = await supabase
        .from('startup_analysis')
        .select('*')
        .eq('startup_id', id)
        .maybeSingle();

      if (cachedAnalysis?.analysis_json) {
        setAnalysis(cachedAnalysis.analysis_json as unknown as Analysis);
        setLoading(false);
        return;
      }

      await generateAnalysis(false);
    } catch (error) {
      console.error('Error fetching analysis:', error);
      toast.error('Failed to load startup details');
      setLoading(false);
    }
  };

  const generateAnalysis = async (forceRefresh = false) => {
    try {
      setGenerating(true);
      
      const { data, error } = await supabase.functions.invoke('analyze-startup', {
        body: { startupId: id, forceRefresh }
      });

      if (error) throw error;

      if (data?.analysis) {
        setAnalysis(data.analysis);
        toast.success(forceRefresh ? 'Analysis regenerated!' : 'Analysis generated!');
      }
    } catch (error) {
      console.error('Error generating analysis:', error);
      toast.error('Failed to generate analysis');
    } finally {
      setGenerating(false);
      setLoading(false);
    }
  };

  const hasBuilderSession = () => {
    const insights = startup?.insights as any;
    return insights?.builderInsights !== undefined && insights?.builderInsights !== null;
  };

  const handleBuildClick = () => {
    if (!user) {
      toast.error('Please sign in to start building this idea');
      navigate('/auth');
      return;
    }
    
    const builderExists = hasBuilderSession();
    navigate(`/build/${id}`);
    toast.success(
      builderExists 
        ? `Continuing build for ${startup?.title}` 
        : `✅ Builder initialized for ${startup?.title}`
    );
  };

  const getMomentumIcon = () => {
    if (!analysis?.metrics) return <Minus className="w-4 h-4" />;
    switch (analysis.metrics.momentumDirection) {
      case 'up': return <TrendingUp className="w-4 h-4 text-green-500" />;
      case 'down': return <TrendingDown className="w-4 h-4 text-red-500" />;
      default: return <Minus className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 8) return 'text-green-600';
    if (score >= 6) return 'text-blue-600';
    if (score >= 4) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreProgress = (score: number) => (score / 10) * 100;

  const fetchLiveTrendData = async (keyword?: string) => {
    if (!analysis && !keyword) return;
    
    const searchKeyword = keyword || selectedKeyword || analysis?.overview.title;
    if (!searchKeyword) return;
    
    setLoadingLiveData(true);
    try {
      const { data, error } = await supabase.functions.invoke('fetch-trends', {
        body: { 
          keyword: searchKeyword,
          timeRange: trendPeriod 
        }
      });

      if (error) throw error;

      if (data && data.timeline) {
        setLiveTrendData(data as LiveTrendData);
        setSelectedKeyword(searchKeyword);
        setTrendLastUpdated(new Date().toISOString());
        toast.success(`Trend data loaded for "${searchKeyword}"`);
      }
    } catch (error) {
      console.error('Error fetching live trends:', error);
      toast.error('Failed to load live trend data');
    } finally {
      setLoadingLiveData(false);
    }
  };

  const fetchLiveKeywordData = async () => {
    if (!analysis) return;
    
    setLoadingLiveData(true);
    try {
      const { data, error } = await supabase.functions.invoke('fetch-keyword-volumes', {
        body: { keywords: analysis.overview.tags.slice(0, 10) }
      });

      if (error) throw error;

      if (data?.keywords) {
        setLiveKeywordData(data as LiveKeywordData);
        toast.success('Live keyword data loaded!');
      }
    } catch (error) {
      console.error('Error fetching keyword volumes:', error);
      toast.error('Failed to load keyword volumes');
    } finally {
      setLoadingLiveData(false);
    }
  };

  const loadAllLiveData = async () => {
    await Promise.all([
      fetchLiveTrendData(),
      fetchLiveKeywordData()
    ]);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8 max-w-7xl">
          <Skeleton className="h-8 w-48 mb-8" />
          <Skeleton className="h-64 w-full mb-8" />
          <div className="grid md:grid-cols-3 gap-6">
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
          </div>
        </div>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="p-8 text-center max-w-md">
          <p className="text-muted-foreground mb-4">No analysis available</p>
          <Button onClick={() => generateAnalysis(true)} disabled={generating}>
            {generating ? 'Generating...' : 'Generate Analysis'}
          </Button>
        </Card>
      </div>
    );
  }

  const trendData = liveTrendData?.timeline || analysis.trendData.curve;
  
  // Calculate trend direction
  const getTrendSummary = () => {
    if (!trendData || trendData.length < 2) return { emoji: '➡️', text: 'Stable demand' };
    
    const recentData = trendData.slice(-Math.min(30, trendData.length));
    const olderData = trendData.slice(0, Math.min(30, trendData.length));
    
    const recentAvg = recentData.reduce((sum, d) => sum + d.value, 0) / recentData.length;
    const olderAvg = olderData.reduce((sum, d) => sum + d.value, 0) / olderData.length;
    
    const percentChange = ((recentAvg - olderAvg) / olderAvg) * 100;
    
    if (percentChange > 15) return { emoji: '📈', text: 'Rising interest this year' };
    if (percentChange < -15) return { emoji: '📉', text: 'Declining trend lately' };
    return { emoji: '➡️', text: 'Stable demand' };
  };
  
  const getMonetizationIcon = (iconType: string) => {
    switch (iconType) {
      case 'subscription':
        return <CreditCard className="w-6 h-6 text-blue-600" />;
      case 'ai-service':
        return <Brain className="w-6 h-6 text-purple-600" />;
      case 'analytics':
        return <BarChart3 className="w-6 h-6 text-green-600" />;
      case 'marketplace':
        return <ShoppingCart className="w-6 h-6 text-orange-600" />;
      case 'freemium':
        return <Users className="w-6 h-6 text-cyan-600" />;
      default:
        return <DollarSign className="w-6 h-6 text-green-600" />;
    }
  };
  
  const getVolumeColor = (volume: number) => {
    if (volume >= 50000) return 'text-green-600';
    if (volume >= 10000) return 'text-yellow-600';
    return 'text-red-600';
  };
  
  const getVolumeBadgeVariant = (volume: number): "default" | "secondary" | "destructive" => {
    if (volume >= 50000) return 'default';
    if (volume >= 10000) return 'secondary';
    return 'destructive';
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={analysis.overview.title}
        description={analysis.insights?.aiSummary || analysis.overview.tagline}
        url={window.location.href}
      />
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          className="mb-6"
          onClick={() => navigate('/startups')}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Explorer
        </Button>

        {/* Top Section - Title, Tags, Actions */}
        <Card className="p-6 mb-6 rounded-2xl shadow-md hover:shadow-lg transition-all animate-fade-in">
          <div className="flex items-start justify-between mb-6">
            <div className="flex-1 pr-8">
              <h1 className="text-4xl font-bold mb-3 bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">
                {analysis.overview.title}
              </h1>
              <p className="text-lg text-muted-foreground mb-4 leading-relaxed">
                {analysis.overview.tagline}
              </p>
              
              <div className="flex flex-wrap gap-2 mb-4">
                <Badge variant="default" className="bg-primary">{analysis.overview.category}</Badge>
                {analysis.overview.tags.slice(0, 5).map((tag, idx) => (
                  <Badge key={idx} variant="secondary">{tag}</Badge>
                ))}
              </div>
              
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Target className="w-4 h-4" />
                  Source: {analysis.overview.source}
                </span>
                {analysis.overview.sourceUrl && (
                  <a 
                    href={analysis.overview.sourceUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline flex items-center gap-1"
                  >
                    View Original <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons - Polished with Gradient Hover */}
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={() => id && createProjectFromIdea(id)}
              disabled={isCreating}
              className="rounded-full px-4 py-2 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-primary-foreground transition-all"
            >
              {isCreating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {progress || 'Building...'}
                </>
              ) : (
                <>
                  <Wand2 className="w-4 h-4 mr-2" />
                  Build With AI
                </>
              )}
            </Button>
            <Button
              onClick={saveToIdeas}
              disabled={isSaved}
              variant={isSaved ? "outline" : "default"}
              className="rounded-full px-4 py-2 bg-muted/30 hover:bg-primary/10 transition-all"
            >
              <Heart className={`w-4 h-4 mr-2 ${isSaved ? "fill-current" : ""} ${isSaved ? 'animate-pulse' : ''}`} />
              {isSaved ? 'Saved to My Ideas' : 'Save to My Ideas'}
            </Button>
            <Button 
              variant="outline" 
              onClick={handleBuildClick}
              className="rounded-full px-4 py-2 bg-muted/30 hover:bg-primary/10 transition-all"
            >
              <Rocket className="w-4 h-4 mr-2" />
              {hasBuilderSession() ? 'Continue Building' : 'Build This Idea'}
            </Button>
            <ShareButton
              url={window.location.href}
              title={analysis.overview.title}
              description={analysis.overview.tagline}
              variant="outline"
              className="rounded-full px-4 py-2 bg-muted/30 hover:bg-primary/10 transition-all"
            />
            <Button
              onClick={() => generateAnalysis(true)}
              disabled={generating}
              variant="outline"
              className="rounded-full px-4 py-2 bg-muted/30 hover:bg-primary/10 transition-all"
            >
              <Sparkles className={`w-4 h-4 mr-2 ${generating ? 'animate-spin' : ''}`} />
              {generating ? 'Generating...' : 'Generate Deep Analysis'}
            </Button>
            <Button 
              variant="outline" 
              className="rounded-full px-4 py-2 bg-muted/30 hover:bg-primary/10 transition-all"
            >
              <Search className="w-4 h-4 mr-2" />
              Research My Idea
            </Button>
            <Button 
              variant="outline" 
              className="rounded-full px-4 py-2 bg-muted/30 hover:bg-primary/10 transition-all"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Get Instant Answers
            </Button>
            <Button 
              variant="outline" 
              onClick={loadAllLiveData}
              disabled={loadingLiveData}
              className="rounded-full px-4 py-2 bg-muted/30 hover:bg-primary/10 transition-all"
            >
              <TrendingUp className={`w-4 h-4 mr-2 ${loadingLiveData ? 'animate-spin' : ''}`} />
              {loadingLiveData ? 'Loading...' : 'Load Live Data'}
            </Button>
          </div>
        </Card>

        {/* Market Signals Row - Quick scanning indicators */}
        <MarketSignalsRow
          monetizationClarity={analysis.insights?.scores?.revenuePotential ? analysis.insights.scores.revenuePotential * 10 : 65}
          demandSignal={analysis.metrics.trendScore > 5 ? Math.min(95, analysis.metrics.trendScore * 12) : 50}
          technicalRisk={analysis.insights?.scores?.executionDifficulty ? analysis.insights.scores.executionDifficulty * 10 : 40}
          founderAdvantage={analysis.insights?.scores?.opportunity ? analysis.insights.scores.opportunity * 10 : 60}
        />

        {/* ARR Highlight Card - Prominent economics overview */}
        {analysis.insights?.arrProjections && analysis.insights?.unitEconomics && (
          <ARRHighlightCard
            year1ARR={analysis.insights.arrProjections.base.year1}
            pricingAssumption={analysis.insights.arrProjections.base.assumptions?.split('.')[0] || "SaaS pricing model"}
            userAcquisition="Organic + paid acquisition"
            grossMargin={analysis.insights.unitEconomics.grossMargin}
          />
        )}

        {/* Compact Metrics Row */}
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <Card className="p-6 rounded-2xl shadow-md hover:shadow-lg transition-all animate-fade-in">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Trend Score</p>
                <p className="text-2xl font-bold text-primary">{analysis.metrics.trendScore.toFixed(1)}</p>
              </div>
              <BarChart3 className="w-8 h-8 text-primary opacity-50" />
            </div>
          </Card>

          <Card className="p-6 rounded-2xl shadow-md hover:shadow-lg transition-all animate-fade-in">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Search Momentum</p>
                <p className="text-2xl font-bold flex items-center gap-2">
                  {analysis.metrics.searchMomentum > 0 ? '+' : ''}
                  {analysis.metrics.searchMomentum.toFixed(1)}%
                  {getMomentumIcon()}
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-accent opacity-50" />
            </div>
          </Card>

          <Card className="p-6 rounded-2xl shadow-md hover:shadow-lg transition-all animate-fade-in">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Last Updated</p>
                <p className="text-sm font-medium">
                  {analysis.generatedAt 
                    ? new Date(analysis.generatedAt).toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })
                    : 'Just now'
                  }
                </p>
              </div>
              <Zap className="w-8 h-8 text-yellow-500 opacity-50" />
            </div>
          </Card>
        </div>

        {/* Trend Over Time Chart - Polished Gradient Area Chart */}
        {trendData && trendData.length > 0 && (
          <Card className="mb-6 rounded-2xl shadow-md hover:shadow-lg transition-all animate-fade-in">
            <CardHeader>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <CardTitle className="text-xl font-semibold text-primary flex items-center gap-2">
                    <Activity className="h-5 w-5" />
                    Trend Over Time
                    {liveTrendData && (
                      <Badge variant="default" className="text-xs ml-2 animate-scale-in">
                        Live
                      </Badge>
                    )}
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    Search interest over selected time period
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Tabs value={trendPeriod} onValueChange={(v) => {
                    setTrendPeriod(v as any);
                    if (liveTrendData) fetchLiveTrendData();
                  }}>
                    <TabsList className="h-9">
                      <TabsTrigger value="6m" className="text-xs">6M</TabsTrigger>
                      <TabsTrigger value="1y" className="text-xs">1Y</TabsTrigger>
                      <TabsTrigger value="5y" className="text-xs">5Y</TabsTrigger>
                    </TabsList>
                  </Tabs>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 hover:bg-primary/10 hover:text-primary transition-colors"
                    onClick={() => fetchLiveTrendData()}
                    disabled={loadingLiveData}
                    title="🔄 Refresh Data"
                  >
                    <RefreshCw className={`h-4 w-4 ${loadingLiveData ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </div>
              
              {/* Trend Summary Box */}
              <div className="mb-4 p-3 rounded-lg bg-primary/5 border border-primary/20">
                <p className="text-sm font-medium text-primary">
                  <span className="mr-2">{getTrendSummary().emoji}</span>
                  {getTrendSummary().text}
                </p>
              </div>
              
              {selectedKeyword && (
                <p className="text-sm text-muted-foreground">
                  Showing trends for: <span className="font-semibold text-primary">{selectedKeyword}</span>
                </p>
              )}
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={340}>
                <AreaChart data={trendData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="rgba(59, 130, 246, 0.35)" />
                      <stop offset="100%" stopColor="rgba(59, 130, 246, 0.05)" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={(value) => {
                      const date = new Date(value);
                      return date.toLocaleDateString('en-US', { month: 'short' });
                    }}
                    stroke="hsl(var(--muted-foreground))"
                    style={{ fontSize: '12px' }}
                  />
                  <YAxis 
                    stroke="hsl(var(--muted-foreground))"
                    style={{ fontSize: '12px' }}
                    label={{ value: 'Interest Index', angle: -90, position: 'insideLeft', style: { fill: 'hsl(var(--muted-foreground))' } }}
                  />
                  <Tooltip 
                    labelFormatter={(value) => new Date(value).toLocaleDateString('en-US', { 
                      year: 'numeric',
                      month: 'long', 
                      day: 'numeric' 
                    })}
                    formatter={(value: number) => [value.toFixed(0), 'Interest']}
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '12px',
                      boxShadow: 'var(--shadow-soft)',
                      padding: '8px 12px'
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="value" 
                    stroke="rgb(59, 130, 246)" 
                    strokeWidth={2.5}
                    fill="url(#colorGradient)"
                    animationDuration={1200}
                    animationEasing="ease-in"
                  />
                </AreaChart>
              </ResponsiveContainer>
              {trendLastUpdated && (
                <p className="text-xs text-muted-foreground text-right mt-3">
                  Updated {new Date(trendLastUpdated).toLocaleString('en-US', { 
                    month: 'short', 
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Business Fit Scores with Progress Bars */}
        {analysis.insights?.scores && (
          <Card className="p-6 mb-6 rounded-2xl shadow-md hover:shadow-lg transition-all animate-fade-in">
            <h2 className="text-xl font-semibold text-primary mb-6">Business Fit Scores</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Opportunity</span>
                  <span className={`text-lg font-bold ${getScoreColor(analysis.insights.scores.opportunity)}`}>
                    {analysis.insights.scores.opportunity}/10
                  </span>
                </div>
                <Progress value={getScoreProgress(analysis.insights.scores.opportunity)} className="h-3" />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Problem Severity</span>
                  <span className={`text-lg font-bold ${getScoreColor(analysis.insights.scores.problem)}`}>
                    {analysis.insights.scores.problem}/10
                  </span>
                </div>
                <Progress value={getScoreProgress(analysis.insights.scores.problem)} className="h-3" />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Feasibility</span>
                  <span className={`text-lg font-bold ${getScoreColor(analysis.insights.scores.feasibility)}`}>
                    {analysis.insights.scores.feasibility}/10
                  </span>
                </div>
                <Progress value={getScoreProgress(analysis.insights.scores.feasibility)} className="h-3" />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Why Now</span>
                  <span className={`text-lg font-bold ${getScoreColor(analysis.insights.scores.whyNowScore)}`}>
                    {analysis.insights.scores.whyNowScore}/10
                  </span>
                </div>
                <Progress value={getScoreProgress(analysis.insights.scores.whyNowScore)} className="h-3" />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Go-to-Market</span>
                  <span className={`text-lg font-bold ${getScoreColor(analysis.insights.scores.goToMarket)}`}>
                    {analysis.insights.scores.goToMarket}/10
                  </span>
                </div>
                <Progress value={getScoreProgress(analysis.insights.scores.goToMarket)} className="h-3" />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Revenue Potential</span>
                  <span className={`text-lg font-bold ${getScoreColor(analysis.insights.scores.revenuePotential)}`}>
                    {analysis.insights.scores.revenuePotential}/10
                  </span>
                </div>
                <Progress value={getScoreProgress(analysis.insights.scores.revenuePotential)} className="h-3" />
              </div>

              <div className="md:col-span-2">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Execution Difficulty</span>
                  <span className={`text-lg font-bold ${getScoreColor(10 - analysis.insights.scores.executionDifficulty)}`}>
                    {analysis.insights.scores.executionDifficulty}/10
                  </span>
                </div>
                <Progress 
                  value={getScoreProgress(analysis.insights.scores.executionDifficulty)} 
                  className="h-3"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Higher score = more difficult (inversely scaled for visualization)
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Why Now Section - Collapsible */}
        {analysis.insights?.whyNow && (
          <CollapsibleSection
            title="Why Now?"
            summary={analysis.insights.whyNow.split('\n\n')[0]?.slice(0, 150) + '...' || "Market timing analysis"}
            icon={TrendingUp}
            iconColor="text-accent"
          >
            <div className="prose prose-sm max-w-none">
              {analysis.insights.whyNow.split('\n\n').map((para, idx) => (
                <p key={idx} className="mb-3 text-foreground/80 leading-relaxed">
                  {para}
                </p>
              ))}
            </div>
          </CollapsibleSection>
        )}

        {/* Proof & Signals - Collapsible */}
        {analysis.insights?.proofSignals && (
          <CollapsibleSection
            title="Proof & Market Signals"
            summary={analysis.insights.proofSignals.split('\n')[0]?.slice(0, 150) + '...' || "Evidence of market demand"}
            icon={Target}
            iconColor="text-primary"
          >
            <div className="prose prose-sm max-w-none">
              {analysis.insights.proofSignals.split('\n').map((line, idx) => {
                if (!line.trim()) return null;
                return (
                  <p key={idx} className="mb-2 text-foreground/80 leading-relaxed">
                    {line}
                  </p>
                );
              })}
            </div>
          </CollapsibleSection>
        )}

        {/* Market Gap - Collapsible */}
        {analysis.insights?.marketGap && (
          <CollapsibleSection
            title="Competitive Landscape & Market Gap"
            summary={analysis.insights.marketGap.split('\n\n')[0]?.slice(0, 150) + '...' || "Opportunity in the market"}
            icon={Crosshair}
            iconColor="text-yellow-500"
          >
            <div className="prose prose-sm max-w-none">
              {analysis.insights.marketGap.split('\n\n').map((para, idx) => (
                <p key={idx} className="mb-3 text-foreground/80 leading-relaxed">
                  {para}
                </p>
              ))}
            </div>
          </CollapsibleSection>
        )}

        {/* Execution Plan */}
        {analysis.insights?.executionPlan && (
          <Card className="p-6 mb-6 rounded-2xl shadow-md hover:shadow-lg transition-all animate-fade-in">
            <h2 className="text-xl font-semibold text-primary mb-6 flex items-center gap-2">
              <Rocket className="w-6 h-6 text-primary" />
              Execution Plan
            </h2>
            
            <div className="space-y-6">
              {analysis.insights.executionPlan.validation && (
                <div className="border-l-4 border-l-blue-500 pl-4">
                  <h3 className="text-lg font-semibold mb-3 text-blue-600">
                    Phase 1: Validation
                  </h3>
                  <ul className="space-y-2">
                    {analysis.insights.executionPlan.validation.map((task, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <span className="text-blue-500 mt-1 font-bold">•</span>
                        <span className="text-foreground/80">{task}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {analysis.insights.executionPlan.mvp && (
                <div className="border-l-4 border-l-green-500 pl-4">
                  <h3 className="text-lg font-semibold mb-3 text-green-600">
                    Phase 2: Build MVP
                  </h3>
                  <ul className="space-y-2">
                    {analysis.insights.executionPlan.mvp.map((task, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <span className="text-green-500 mt-1 font-bold">•</span>
                        <span className="text-foreground/80">{task}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {analysis.insights.executionPlan.growth && (
                <div className="border-l-4 border-l-purple-500 pl-4">
                  <h3 className="text-lg font-semibold mb-3 text-purple-600">
                    Phase 3: Growth & Scale
                  </h3>
                  <ul className="space-y-2">
                    {analysis.insights.executionPlan.growth.map((task, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <span className="text-purple-500 mt-1 font-bold">•</span>
                        <span className="text-foreground/80">{task}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Monetization Opportunities */}
        {analysis.insights?.monetizationOpportunities && analysis.insights.monetizationOpportunities.length > 0 && (
          <Card className="p-6 mb-6 rounded-2xl shadow-md hover:shadow-lg transition-all animate-fade-in">
            <h2 className="text-xl font-semibold text-primary mb-2 flex items-center gap-2">
              <DollarSign className="w-6 h-6 text-green-600" />
              Monetization Opportunities
            </h2>
            <p className="text-sm text-muted-foreground mb-6">
              Top 3 realistic revenue streams for this startup concept
            </p>
            
            <div className="grid md:grid-cols-3 gap-4">
              {analysis.insights.monetizationOpportunities.map((model, idx) => (
                <Card key={idx} className="p-5 rounded-xl bg-gradient-to-br from-card to-muted/20 border-2 hover:border-primary/30 transition-all hover-scale">
                  <div className="flex items-center gap-3 mb-4">
                    {getMonetizationIcon(model.iconType)}
                    <h3 className="font-semibold text-base text-foreground">{model.name}</h3>
                  </div>
                  <p className="text-sm text-foreground/70 mb-3 leading-relaxed">
                    {model.description}
                  </p>
                  <div className="space-y-2 text-xs">
                    <div className="flex items-start gap-2">
                      <span className="font-semibold text-primary shrink-0">💰 Pricing:</span>
                      <span className="text-foreground/70">{model.pricing}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="font-semibold text-primary shrink-0">📈 Scale:</span>
                      <span className="text-foreground/70">{model.scalability}</span>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </Card>
        )}

        {/* Market Signals */}
        {analysis.insights?.marketSignals && (
          <Card className="p-6 mb-6 rounded-2xl shadow-md hover:shadow-lg transition-all animate-fade-in">
            <h2 className="text-xl font-semibold text-primary mb-2 flex items-center gap-2">
              <Briefcase className="w-6 h-6 text-blue-600" />
              Market Signals
            </h2>
            <p className="text-sm text-muted-foreground mb-6">
              Recent market patterns, investor activity, and adoption indicators
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* Left: Summary */}
              <div className="flex flex-col justify-center">
                <p className="text-sm text-foreground/80 leading-relaxed">
                  {analysis.insights.marketSignals.summary}
                </p>
              </div>
              
              {/* Right: Signal Cards */}
              <div className="space-y-3">
                {analysis.insights.marketSignals.signals.map((signal, idx) => (
                  <div 
                    key={idx}
                    className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 border border-border/50 hover:bg-primary/5 transition-colors"
                  >
                    <span className="text-lg shrink-0">{signal.split(' ')[0]}</span>
                    <p className="text-sm text-foreground/70 flex-1">
                      {signal.substring(signal.indexOf(' ') + 1)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        )}

        {/* AI Summary */}
        {analysis.insights?.aiSummary && (
          <Card className="p-6 mb-6 rounded-2xl shadow-md hover:shadow-lg transition-all animate-fade-in bg-gradient-to-br from-primary/5 via-card to-muted/10">
            <h2 className="text-xl font-semibold text-primary mb-2 flex items-center gap-2">
              <Brain className="w-6 h-6 text-purple-600" />
              AI Summary
            </h2>
            <p className="text-sm text-muted-foreground mb-4">
              Concise snapshot of this idea's potential
            </p>
            
            <p className="text-base text-foreground/80 leading-relaxed mb-4">
              {analysis.insights.aiSummary}
            </p>
            
            <p className="text-xs italic text-muted-foreground text-center">
              Generated by DayOne
            </p>
          </Card>
        )}

        {/* Framework Fit - 3 mini cards */}
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          {/* Value Equation */}
          {analysis.insights?.valueEquation && (
            <Card className="p-6 rounded-2xl shadow-md hover:shadow-lg transition-all animate-fade-in">
              <h3 className="text-base font-semibold text-primary mb-3 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-600" />
                Value Equation
              </h3>
              <p className="text-sm text-foreground/70 font-mono bg-muted/50 p-3 rounded leading-relaxed">
                {analysis.insights.valueEquation}
              </p>
            </Card>
          )}
        </div>

        {/* ARR Projections & Unit Economics */}
        {analysis.insights?.arrProjections && (
          <Card className="p-6 mb-6 rounded-2xl shadow-md hover:shadow-lg transition-all animate-fade-in">
            <h2 className="text-xl font-semibold text-primary mb-2 flex items-center gap-2">
              <Calculator className="w-6 h-6 text-green-600" />
              Estimated ARR & Unit Economics
            </h2>
            <p className="text-sm text-muted-foreground mb-6">
              Revenue projections across conservative, base, and aggressive scenarios
            </p>
            
            {/* ARR Projections Table */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4">ARR Projections</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 font-semibold">Scenario</th>
                      <th className="text-right py-3 px-4 font-semibold">Year 1</th>
                      <th className="text-right py-3 px-4 font-semibold">Year 2</th>
                      <th className="text-right py-3 px-4 font-semibold">Year 3</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-border/50 hover:bg-muted/30">
                      <td className="py-3 px-4">
                        <span className="font-medium text-yellow-600">Conservative</span>
                      </td>
                      <td className="text-right py-3 px-4 font-mono">{analysis.insights.arrProjections.conservative.year1}</td>
                      <td className="text-right py-3 px-4 font-mono">{analysis.insights.arrProjections.conservative.year2}</td>
                      <td className="text-right py-3 px-4 font-mono">{analysis.insights.arrProjections.conservative.year3}</td>
                    </tr>
                    <tr className="border-b border-border/50 hover:bg-muted/30 bg-primary/5">
                      <td className="py-3 px-4">
                        <span className="font-medium text-primary">Base Case</span>
                      </td>
                      <td className="text-right py-3 px-4 font-mono font-semibold">{analysis.insights.arrProjections.base.year1}</td>
                      <td className="text-right py-3 px-4 font-mono font-semibold">{analysis.insights.arrProjections.base.year2}</td>
                      <td className="text-right py-3 px-4 font-mono font-semibold">{analysis.insights.arrProjections.base.year3}</td>
                    </tr>
                    <tr className="hover:bg-muted/30">
                      <td className="py-3 px-4">
                        <span className="font-medium text-green-600">Aggressive</span>
                      </td>
                      <td className="text-right py-3 px-4 font-mono">{analysis.insights.arrProjections.aggressive.year1}</td>
                      <td className="text-right py-3 px-4 font-mono">{analysis.insights.arrProjections.aggressive.year2}</td>
                      <td className="text-right py-3 px-4 font-mono">{analysis.insights.arrProjections.aggressive.year3}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              {/* Assumptions */}
              <div className="mt-6 space-y-3">
                <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                  <p className="text-sm font-semibold text-yellow-700 dark:text-yellow-400 mb-1">Conservative Assumptions</p>
                  <p className="text-sm text-foreground/70">{analysis.insights.arrProjections.conservative.assumptions}</p>
                </div>
                <div className="p-4 rounded-lg bg-primary/10 border border-primary/20">
                  <p className="text-sm font-semibold text-primary mb-1">Base Case Assumptions</p>
                  <p className="text-sm text-foreground/70">{analysis.insights.arrProjections.base.assumptions}</p>
                </div>
                <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                  <p className="text-sm font-semibold text-green-700 dark:text-green-400 mb-1">Aggressive Assumptions</p>
                  <p className="text-sm text-foreground/70">{analysis.insights.arrProjections.aggressive.assumptions}</p>
                </div>
              </div>
            </div>
            
            {/* Unit Economics */}
            {analysis.insights.unitEconomics && (
              <div>
                <h3 className="text-lg font-semibold mb-4">Unit Economics</h3>
                <div className="grid md:grid-cols-5 gap-4 mb-6">
                  <div className="p-4 bg-muted/30 rounded-lg text-center">
                    <p className="text-xs text-muted-foreground mb-1">LTV</p>
                    <p className="text-lg font-bold text-primary">{analysis.insights.unitEconomics.ltv.split(':')[0].replace('Estimated LTV', '').trim() || analysis.insights.unitEconomics.ltv}</p>
                  </div>
                  <div className="p-4 bg-muted/30 rounded-lg text-center">
                    <p className="text-xs text-muted-foreground mb-1">Target CAC</p>
                    <p className="text-lg font-bold text-primary">{analysis.insights.unitEconomics.cac.split(':')[0].replace('Target CAC', '').trim() || analysis.insights.unitEconomics.cac}</p>
                  </div>
                  <div className="p-4 bg-muted/30 rounded-lg text-center">
                    <p className="text-xs text-muted-foreground mb-1">LTV:CAC</p>
                    <p className="text-lg font-bold text-green-600">{analysis.insights.unitEconomics.ltvCacRatio}</p>
                  </div>
                  <div className="p-4 bg-muted/30 rounded-lg text-center">
                    <p className="text-xs text-muted-foreground mb-1">Payback Period</p>
                    <p className="text-lg font-bold text-primary">{analysis.insights.unitEconomics.paybackPeriod}</p>
                  </div>
                  <div className="p-4 bg-muted/30 rounded-lg text-center">
                    <p className="text-xs text-muted-foreground mb-1">Gross Margin</p>
                    <p className="text-lg font-bold text-primary">{analysis.insights.unitEconomics.grossMargin}</p>
                  </div>
                </div>
                <div className="p-4 rounded-lg bg-muted/20 border border-border/50">
                  <p className="text-sm text-foreground/80 leading-relaxed whitespace-pre-line">{analysis.insights.unitEconomics.explanation}</p>
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Go-To-Market Strategy - Collapsible */}
        {analysis.insights?.goToMarketStrategy && (
          <CollapsibleSection
            title="Go-To-Market Strategy"
            summary={`${analysis.insights.goToMarketStrategy.primaryChannels?.length || 0} acquisition channels • ${analysis.insights.goToMarketStrategy.icpDefinition?.slice(0, 80)}...`}
            icon={Megaphone}
            iconColor="text-orange-600"
          >
            {/* Primary Channels */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4">Primary Acquisition Channels</h3>
              <div className="grid md:grid-cols-3 gap-4">
                {analysis.insights.goToMarketStrategy.primaryChannels.map((channel, idx) => (
                  <Card key={idx} className="p-5 rounded-xl bg-gradient-to-br from-card to-muted/20 border-2 hover:border-primary/30 transition-all">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-lg font-bold text-primary">{idx + 1}</span>
                      </div>
                      <h4 className="font-semibold text-base">{channel.channel}</h4>
                    </div>
                    <p className="text-sm text-foreground/70 leading-relaxed">{channel.description}</p>
                  </Card>
                ))}
              </div>
            </div>
            
            {/* ICP Definition */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-blue-600" />
                Ideal Customer Profile (ICP)
              </h3>
              <div className="p-5 rounded-lg bg-blue-500/5 border border-blue-500/20">
                <p className="text-sm text-foreground/80 leading-relaxed whitespace-pre-line">{analysis.insights.goToMarketStrategy.icpDefinition}</p>
              </div>
            </div>
            
            {/* GTM Fit */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4">Why This GTM Fits</h3>
              <div className="p-5 rounded-lg bg-muted/20 border border-border/50">
                <p className="text-sm text-foreground/80 leading-relaxed whitespace-pre-line">{analysis.insights.goToMarketStrategy.gtmFit}</p>
              </div>
            </div>
            
            {/* Launch Strategy */}
            <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Rocket className="w-5 h-5 text-purple-600" />
                Launch Strategy
              </h3>
              <div className="p-5 rounded-lg bg-purple-500/5 border border-purple-500/20">
                <p className="text-sm text-foreground/80 leading-relaxed whitespace-pre-line">{analysis.insights.goToMarketStrategy.launchStrategy}</p>
              </div>
            </div>
          </CollapsibleSection>
        )}

        {/* Estimated Costs */}
        {analysis.insights?.estimatedCosts && (
          <Card className="p-6 mb-6 rounded-2xl shadow-md hover:shadow-lg transition-all animate-fade-in">
            <h2 className="text-xl font-semibold text-primary mb-2 flex items-center gap-2">
              <PiggyBank className="w-6 h-6 text-pink-600" />
              Estimated Costs
            </h2>
            <p className="text-sm text-muted-foreground mb-6">
              MVP build costs, monthly operating expenses, and tooling stack
            </p>
            
            {/* Cost Summary Cards */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="p-5 rounded-xl bg-gradient-to-br from-green-500/10 to-green-500/5 border border-green-500/20">
                <div className="flex items-center gap-3 mb-3">
                  <Server className="w-6 h-6 text-green-600" />
                  <h3 className="font-semibold">MVP Build Cost</h3>
                </div>
                <p className="text-3xl font-bold text-green-600 mb-3">{analysis.insights.estimatedCosts.mvpBuildCost.range}</p>
                <p className="text-sm text-foreground/70 leading-relaxed">{analysis.insights.estimatedCosts.mvpBuildCost.breakdown}</p>
              </div>
              
              <div className="p-5 rounded-xl bg-gradient-to-br from-blue-500/10 to-blue-500/5 border border-blue-500/20">
                <div className="flex items-center gap-3 mb-3">
                  <Clock className="w-6 h-6 text-blue-600" />
                  <h3 className="font-semibold">Monthly Operating</h3>
                </div>
                <p className="text-3xl font-bold text-blue-600 mb-3">{analysis.insights.estimatedCosts.monthlyOperating.range}</p>
                <p className="text-sm text-foreground/70 leading-relaxed">{analysis.insights.estimatedCosts.monthlyOperating.breakdown}</p>
              </div>
            </div>
            
            {/* Tooling Stack */}
            {analysis.insights.estimatedCosts.toolingStack && analysis.insights.estimatedCosts.toolingStack.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-4">Recommended Tooling Stack</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4 font-semibold">Category</th>
                        <th className="text-left py-3 px-4 font-semibold">Tools</th>
                        <th className="text-right py-3 px-4 font-semibold">Est. Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analysis.insights.estimatedCosts.toolingStack.map((tool, idx) => (
                        <tr key={idx} className="border-b border-border/50 hover:bg-muted/30">
                          <td className="py-3 px-4 font-medium">{tool.category}</td>
                          <td className="py-3 px-4 text-foreground/70">{tool.tools}</td>
                          <td className="text-right py-3 px-4 font-mono text-primary">{tool.cost}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            
            {/* Runway Calculation */}
            <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
              <p className="text-sm font-semibold text-yellow-700 dark:text-yellow-400 mb-1">💰 Runway Calculation</p>
              <p className="text-sm text-foreground/80">{analysis.insights.estimatedCosts.runwayCalculation}</p>
            </div>
          </Card>
        )}

        {/* Risks & Failure Modes - Collapsible */}
        {analysis.insights?.risksAndFailureModes && (
          <CollapsibleSection
            title="Risks & Failure Modes"
            summary={analysis.insights.risksAndFailureModes.summary?.slice(0, 150) + '...' || "Key risks and mitigation strategies"}
            icon={AlertTriangle}
            iconColor="text-red-600"
          >
            {/* Risk Summary */}
            <div className="mb-8 p-5 rounded-lg bg-red-500/5 border border-red-500/20">
              <p className="text-sm text-foreground/80 leading-relaxed whitespace-pre-line">{analysis.insights.risksAndFailureModes.summary}</p>
            </div>
            
            {/* Risk Categories */}
            <div className="space-y-6">
              {/* Market Risks */}
              {analysis.insights.risksAndFailureModes.marketRisks && analysis.insights.risksAndFailureModes.marketRisks.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-orange-600" />
                    Market Risks
                  </h3>
                  <div className="space-y-3">
                    {analysis.insights.risksAndFailureModes.marketRisks.map((risk, idx) => (
                      <div key={idx} className="p-4 rounded-lg bg-muted/20 border border-border/50">
                        <div className="flex items-start justify-between mb-2">
                          <p className="font-semibold text-foreground">{risk.risk}</p>
                          <div className="flex gap-2">
                            <Badge variant={risk.likelihood === 'High' ? 'destructive' : risk.likelihood === 'Medium' ? 'secondary' : 'outline'} className="text-xs">
                              {risk.likelihood} Likelihood
                            </Badge>
                            <Badge variant={risk.impact === 'High' ? 'destructive' : risk.impact === 'Medium' ? 'secondary' : 'outline'} className="text-xs">
                              {risk.impact} Impact
                            </Badge>
                          </div>
                        </div>
                        <p className="text-sm text-foreground/70"><span className="font-medium text-green-600">Mitigation:</span> {risk.mitigation}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Execution Risks */}
              {analysis.insights.risksAndFailureModes.executionRisks && analysis.insights.risksAndFailureModes.executionRisks.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-blue-600" />
                    Execution Risks
                  </h3>
                  <div className="space-y-3">
                    {analysis.insights.risksAndFailureModes.executionRisks.map((risk, idx) => (
                      <div key={idx} className="p-4 rounded-lg bg-muted/20 border border-border/50">
                        <div className="flex items-start justify-between mb-2">
                          <p className="font-semibold text-foreground">{risk.risk}</p>
                          <div className="flex gap-2">
                            <Badge variant={risk.likelihood === 'High' ? 'destructive' : risk.likelihood === 'Medium' ? 'secondary' : 'outline'} className="text-xs">
                              {risk.likelihood} Likelihood
                            </Badge>
                            <Badge variant={risk.impact === 'High' ? 'destructive' : risk.impact === 'Medium' ? 'secondary' : 'outline'} className="text-xs">
                              {risk.impact} Impact
                            </Badge>
                          </div>
                        </div>
                        <p className="text-sm text-foreground/70"><span className="font-medium text-green-600">Mitigation:</span> {risk.mitigation}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Pricing Risks */}
              {analysis.insights.risksAndFailureModes.pricingRisks && analysis.insights.risksAndFailureModes.pricingRisks.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-green-600" />
                    Pricing Risks
                  </h3>
                  <div className="space-y-3">
                    {analysis.insights.risksAndFailureModes.pricingRisks.map((risk, idx) => (
                      <div key={idx} className="p-4 rounded-lg bg-muted/20 border border-border/50">
                        <div className="flex items-start justify-between mb-2">
                          <p className="font-semibold text-foreground">{risk.risk}</p>
                          <div className="flex gap-2">
                            <Badge variant={risk.likelihood === 'High' ? 'destructive' : risk.likelihood === 'Medium' ? 'secondary' : 'outline'} className="text-xs">
                              {risk.likelihood} Likelihood
                            </Badge>
                            <Badge variant={risk.impact === 'High' ? 'destructive' : risk.impact === 'Medium' ? 'secondary' : 'outline'} className="text-xs">
                              {risk.impact} Impact
                            </Badge>
                          </div>
                        </div>
                        <p className="text-sm text-foreground/70"><span className="font-medium text-green-600">Mitigation:</span> {risk.mitigation}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Competitive Risks */}
              {analysis.insights.risksAndFailureModes.competitiveRisks && analysis.insights.risksAndFailureModes.competitiveRisks.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <Users className="w-5 h-5 text-purple-600" />
                    Competitive Risks
                  </h3>
                  <div className="space-y-3">
                    {analysis.insights.risksAndFailureModes.competitiveRisks.map((risk, idx) => (
                      <div key={idx} className="p-4 rounded-lg bg-muted/20 border border-border/50">
                        <div className="flex items-start justify-between mb-2">
                          <p className="font-semibold text-foreground">{risk.risk}</p>
                          <div className="flex gap-2">
                            <Badge variant={risk.likelihood === 'High' ? 'destructive' : risk.likelihood === 'Medium' ? 'secondary' : 'outline'} className="text-xs">
                              {risk.likelihood} Likelihood
                            </Badge>
                            <Badge variant={risk.impact === 'High' ? 'destructive' : risk.impact === 'Medium' ? 'secondary' : 'outline'} className="text-xs">
                              {risk.impact} Impact
                            </Badge>
                          </div>
                        </div>
                        <p className="text-sm text-foreground/70"><span className="font-medium text-green-600">Mitigation:</span> {risk.mitigation}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CollapsibleSection>
        )}

        {/* Framework Fit - 3 mini cards */}
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          {/* Value Equation */}
          {analysis.insights?.valueEquation && (
            <Card className="p-6 rounded-2xl shadow-md hover:shadow-lg transition-all animate-fade-in">
              <h3 className="text-base font-semibold text-primary mb-3 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-600" />
                Value Equation
              </h3>
              <p className="text-sm text-foreground/70 font-mono bg-muted/50 p-3 rounded leading-relaxed">
                {analysis.insights.valueEquation}
              </p>
            </Card>
          )}

          {/* A.C.P. Framework */}
          {analysis.insights?.acpFramework && (
            <Card className="p-6 rounded-2xl shadow-md hover:shadow-lg transition-all animate-fade-in">
              <h3 className="text-base font-semibold text-primary mb-3 flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" />
                A.C.P. Framework
              </h3>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="font-semibold text-primary mb-1">Authority</p>
                  <p className="text-foreground/70 text-xs line-clamp-2">
                    {analysis.insights.acpFramework.authority}
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-primary mb-1">Community</p>
                  <p className="text-foreground/70 text-xs line-clamp-2">
                    {analysis.insights.acpFramework.community}
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-primary mb-1">Product</p>
                  <p className="text-foreground/70 text-xs line-clamp-2">
                    {analysis.insights.acpFramework.product}
                  </p>
                </div>
              </div>
            </Card>
          )}

          {/* Value Ladder */}
          {analysis.insights?.valueLadder && analysis.insights.valueLadder.length > 0 && (
            <Card className="p-6 rounded-2xl shadow-md hover:shadow-lg transition-all animate-fade-in">
              <h3 className="text-base font-semibold text-primary mb-3 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-purple-600" />
                Value Ladder
              </h3>
              <div className="space-y-2">
                {analysis.insights.valueLadder.map((tier, idx) => (
                  <div key={idx} className="text-sm">
                    <p className="font-semibold text-primary">{tier.tier}</p>
                    <p className="text-foreground/70 text-xs line-clamp-1">{tier.offering}</p>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>

        {/* Market Size */}
        {(analysis.insights?.tam || analysis.insights?.sam || analysis.insights?.mrrRange) && (
          <Card className="p-6 mb-6 rounded-2xl shadow-md hover:shadow-lg transition-all animate-fade-in">
            <h2 className="text-xl font-semibold text-primary mb-4 flex items-center gap-2">
              <DollarSign className="w-6 h-6 text-green-600" />
              Market Opportunity
            </h2>
            
            <div className="grid md:grid-cols-3 gap-4">
              {analysis.insights.tam && (
                <div className="p-4 bg-muted/30 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">Total Addressable Market</p>
                  <p className="text-2xl font-bold text-primary">{analysis.insights.tam}</p>
                </div>
              )}
              {analysis.insights.sam && (
                <div className="p-4 bg-muted/30 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">Serviceable Market</p>
                  <p className="text-2xl font-bold text-primary">{analysis.insights.sam}</p>
                </div>
              )}
              {analysis.insights.mrrRange && (
                <div className="p-4 bg-muted/30 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">MRR Potential</p>
                  <p className="text-2xl font-bold text-primary">{analysis.insights.mrrRange}</p>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Live Keyword Insights - Polished Ranked Display */}
        {liveKeywordData && liveKeywordData.keywords && liveKeywordData.keywords.length > 0 && (
          <Card className="mb-6 rounded-2xl shadow-md hover:shadow-lg transition-all animate-fade-in">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-xl font-semibold text-primary flex items-center gap-2">
                    <BarChart3 className="h-5 w-5" />
                    Live Keyword Insights
                    <Badge variant="default" className="text-xs ml-2 animate-scale-in">
                      Live
                    </Badge>
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    Top real-time keywords by search volume and CPC
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 hover:bg-primary/10 hover:text-primary transition-colors"
                  onClick={fetchLiveKeywordData}
                  disabled={loadingLiveData}
                  title="🔄 Refresh Data"
                >
                  <RefreshCw className={`h-4 w-4 ${loadingLiveData ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </CardHeader>
            <CardContent>

              <div className="space-y-0 divide-y divide-border/50">
              {liveKeywordData.keywords.slice(0, 10).map((kw, idx) => {
                const volumeColor = kw.volume >= 50000 ? 'text-green-600' : kw.volume >= 10000 ? 'text-yellow-600' : 'text-red-600';
                const volumeBadge = kw.volume >= 50000 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 
                                   kw.volume >= 10000 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' : 
                                   'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
                
                return (
                  <button
                    key={idx}
                    onClick={() => fetchLiveTrendData(kw.name)}
                    disabled={loadingLiveData}
                    className={`
                      w-full p-4 transition-all duration-300 text-left hover-scale
                      hover:bg-primary/5 hover:scale-[1.01]
                      ${selectedKeyword === kw.name ? 'bg-primary/10' : ''}
                      ${loadingLiveData ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                    `}
                  >
                    <div className="flex items-center gap-4">
                      {/* Rank Number */}
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-primary to-primary-glow flex items-center justify-center shadow-sm">
                        <span className="text-sm font-bold text-primary-foreground">{idx + 1}</span>
                      </div>
                      
                      {/* Keyword Name */}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-base truncate text-foreground">{kw.name}</h4>
                        {selectedKeyword === kw.name && (
                          <p className="text-xs text-primary font-medium">
                            ✓ Viewing trends
                          </p>
                        )}
                      </div>

                      {/* Volume */}
                      <div className="flex items-center gap-2">
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">Volume</p>
                          <p className={`text-base font-bold ${volumeColor}`}>
                            {kw.volume >= 1000000 
                              ? `${(kw.volume / 1000000).toFixed(1)}M` 
                              : kw.volume >= 1000 
                              ? `${(kw.volume / 1000).toFixed(1)}K` 
                              : kw.volume}
                          </p>
                        </div>
                        <Badge className={`${volumeBadge} text-xs px-2 py-1`}>
                          {kw.volume >= 50000 ? 'High' : kw.volume >= 10000 ? 'Med' : 'Low'}
                        </Badge>
                      </div>

                      {/* CPC */}
                      <div className="text-right min-w-[70px]">
                        <p className="text-xs text-muted-foreground">CPC</p>
                        <p className="text-base font-bold text-green-600">
                          ${kw.cpc.toFixed(2)}
                        </p>
                      </div>

                      {/* Competition Bar */}
                      <div className="flex-shrink-0 w-28">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-xs text-muted-foreground">Competition</p>
                          <span className="text-xs font-semibold text-foreground">{kw.competition}</span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-blue-400 to-blue-600 transition-all duration-500"
                            style={{ width: `${kw.competition}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

              <p className="text-xs text-muted-foreground mt-4 text-center">
                💡 Click any keyword to view its trend chart above
              </p>
            </CardContent>
          </Card>
        )}

        {/* Load Keywords Button - Show if no keyword data yet */}
        {!liveKeywordData && analysis.overview.tags && analysis.overview.tags.length > 0 && (
          <Card className="mb-6 rounded-2xl shadow-md hover:shadow-lg transition-all text-center animate-fade-in">
            <CardContent className="pt-6 p-6">
              <BarChart3 className="w-12 h-12 mx-auto mb-3 text-primary/50" />
              <h3 className="text-lg font-semibold mb-2">Load Live Keyword Data</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Get real-time search volumes, CPC, and competition metrics
              </p>
              <Button
                onClick={fetchLiveKeywordData}
                disabled={loadingLiveData}
                size="lg"
                className="hover-scale"
              >
                <Search className="w-4 h-4 mr-2" />
                {loadingLiveData ? 'Loading...' : 'Load Keyword Volumes'}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* YouTube Signals */}
        {analysis.externalData?.youtube.videos && analysis.externalData.youtube.videos.length > 0 && (
          <Card className="p-6 mb-6 rounded-2xl shadow-md hover:shadow-lg transition-all animate-fade-in">
            <div className="mb-4">
              <h2 className="text-xl font-semibold text-primary mb-1 flex items-center gap-2">
                <Youtube className="w-6 h-6 text-red-500" />
                YouTube Signals
              </h2>
              <p className="text-sm text-muted-foreground">
                Top YouTube videos for this niche
                {analysis.externalData.youtube.query && ` (query: "${analysis.externalData.youtube.query}")`}
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {analysis.externalData.youtube.videos.slice(0, 6).map((video) => (
                <a
                  key={video.videoId}
                  href={video.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group"
                >
                  <Card className="overflow-hidden hover:shadow-md transition-all">
                    <div className="relative aspect-video bg-muted">
                      <img 
                        src={video.thumbnail} 
                        alt={video.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <Youtube className="w-12 h-12 text-white" />
                      </div>
                    </div>
                    <div className="p-3">
                      <p className="font-medium text-sm line-clamp-2 mb-1 group-hover:text-primary transition-colors">
                        {video.title}
                      </p>
                      <p className="text-xs text-muted-foreground">{video.channelTitle}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(video.publishedAt).toLocaleDateString()}
                      </p>
                    </div>
                  </Card>
                </a>
              ))}
            </div>

            {analysis.externalData.youtube.videos.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <Youtube className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>No YouTube videos found for this startup</p>
              </div>
            )}
          </Card>
        )}

        {/* News Signals */}
        {analysis.externalData?.news.articles && analysis.externalData.news.articles.length > 0 && (
          <Card className="p-6 mb-6 rounded-2xl shadow-md hover:shadow-lg transition-all animate-fade-in">
            <div className="mb-4">
              <h2 className="text-xl font-semibold text-primary mb-1 flex items-center gap-2">
                <Newspaper className="w-6 h-6 text-blue-600" />
                Recent News
              </h2>
              <p className="text-sm text-muted-foreground">
                Recent articles about this market
                {analysis.externalData.news.query && ` (query: "${analysis.externalData.news.query}")`}
              </p>
            </div>
            
            <div className="space-y-3">
              {analysis.externalData.news.articles.slice(0, 5).map((article, idx) => (
                <a
                  key={idx}
                  href={article.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block group"
                >
                  <Card className="p-4 hover:shadow-md transition-all">
                    <div className="flex gap-4">
                      {article.urlToImage && (
                        <img 
                          src={article.urlToImage} 
                          alt={article.title}
                          className="w-24 h-24 object-cover rounded flex-shrink-0"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium mb-1 group-hover:text-primary transition-colors line-clamp-2">
                          {article.title}
                        </p>
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                          {article.description}
                        </p>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span className="font-medium">{article.source}</span>
                          <span>•</span>
                          <span>{new Date(article.publishedAt).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                  </Card>
                </a>
              ))}
            </div>

            {analysis.externalData.news.articles.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <Newspaper className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>No news articles found for this startup</p>
              </div>
            )}
          </Card>
        )}

        {/* Business Fit Summary */}
        {analysis.insights?.businessFit && (
          <Card className="p-6 mb-6 rounded-2xl shadow-md hover:shadow-lg transition-all animate-fade-in">
            <h2 className="text-xl font-semibold text-primary mb-4 flex items-center gap-2">
              <Users className="w-6 h-6 text-blue-600" />
              Ideal Founder Profile
            </h2>
            <p className="text-foreground/80 leading-relaxed">{analysis.insights.businessFit}</p>
          </Card>
        )}

        {/* Action Panel */}
        <Card className="p-6 shadow-[var(--shadow-medium)] border-2 border-primary/20">
          <h3 className="text-xl font-bold mb-4">Ready to Take Action?</h3>
          <div className="grid md:grid-cols-2 gap-3">
            <Button 
              size="lg" 
              className="w-full"
              onClick={handleBuildClick}
            >
              <Rocket className="w-4 h-4 mr-2" />
              {hasBuilderSession() ? 'Continue Building' : 'Build This Idea'}
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="w-full"
              onClick={() => generateAnalysis(true)}
              disabled={generating}
            >
              <Sparkles className="w-4 h-4 mr-2" />
              {generating ? 'Regenerating...' : 'Regenerate Analysis'}
            </Button>
            <Button size="lg" variant="outline" className="w-full">
              <Search className="w-4 h-4 mr-2" />
              Deep Market Research
            </Button>
            <Button size="lg" variant="outline" className="w-full">
              <MessageSquare className="w-4 h-4 mr-2" />
              Ask AI Questions
            </Button>
          </div>
        </Card>

        {/* Data Provenance */}
        <div className="mt-6 pt-6 border-t text-xs text-muted-foreground text-center space-y-1">
          <p>Market signals aggregated from product launches, search trends, news, and public market research.</p>
          <p>Generated by DayOne</p>
        </div>
      </div>
    </div>
  );
}
